import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { Box, Card, CardContent, Typography, CircularProgress } from '@mui/material'
import { PlatinumData } from '../../types/carbon/models/PlatinumData'
import { GraphConfig } from '../platinum-graphs/types'
import PlatinumGraphScreenshot from '../platinum-graphs/PlatinumGraphScreenshot'
import { extractRowLabels, extractColumnLabels, extractRowTypes, extractColumnTypes, filterNonBaseFields } from '../platinum-graphs/utils/convertPlatinumData'
import { generateGraphOptions } from '../platinum-graphs/utils/generateGraphOptions'
import { API_BASE_URL } from '../../constants/system'
import useRouter from '../../hooks/useRouter'
import { namesToIndexes, nameToIndex } from '../../utils/chartSelections'

interface MultiTableGraphProps {
  customer: string
  job: string
  tablePath: string
  graphConfig: GraphConfig
  explanation?: string
  messageIndex: number
  graphIndex: number
  graphScreenshots: Map<number, string>
  setGraphScreenshots: React.Dispatch<React.SetStateAction<Map<number, string>>>
  onClose?: () => void // Called when navigating away to close the drawer
}

/**
 * MultiTableGraph Component
 *
 * Fetches PlatinumData on-demand for a specific table and renders a graph.
 * Uses React Query for caching and loading state management.
 */
const MultiTableGraph: React.FC<MultiTableGraphProps> = ({
  customer,
  job,
  tablePath,
  graphConfig: initialConfig,
  explanation,
  messageIndex,
  graphIndex,
  graphScreenshots,
  setGraphScreenshots,
  onClose,
}) => {
  const router = useRouter()
  const [validatedConfig, setValidatedConfig] = useState<GraphConfig | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Extract display name from table path (last segment without .cbt)
  const tableDisplayName = useMemo(() => {
    const pathWithoutExt = tablePath.replace(/\.cbt$/, '')
    const segments = pathWithoutExt.split('/')
    return segments[segments.length - 1] || tablePath
  }, [tablePath])

  // Fetch PlatinumData on-demand
  const tableDataQuery = useQuery({
    queryKey: ['multi-table-graph-data', { customer, job, tablePath }],
    enabled: Boolean(customer && job && tablePath),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    queryFn: async () => {
      const res = await axios.get<PlatinumData>(`${API_BASE_URL}/tables/${customer}/${job}/data`, {
        params: {
          path: tablePath,
          show_freq: true,
          show_colpct: true,
        },
      })
      return res.data
    },
  })

  // Validate and normalize config once data is loaded
  React.useEffect(() => {
    if (!tableDataQuery.data) return

    const data = tableDataQuery.data

    // Extract row and column labels
    const allRows = extractRowLabels(data)
    const allColumns = extractColumnLabels(data)
    const allRowTypes = extractRowTypes(data)
    const allColumnTypes = extractColumnTypes(data)

    // Get non-Base fields for default selections
    const nonBaseRows = filterNonBaseFields(allRows, allRowTypes)
    const nonBaseColumns = filterNonBaseFields(allColumns, allColumnTypes)

    // Create a copy of the config to normalize
    const config = { ...initialConfig }

    // Auto-detect if AI confused rows and columns
    const columnsAreActuallyRows = config.selectedColumns?.some(col => allRows.includes(col))
    const rowsAreActuallyColumns = config.selectedRows?.some(row => allColumns.includes(row))

    if (columnsAreActuallyRows || rowsAreActuallyColumns) {
      console.log(`[MultiTableGraph] Detected row/column confusion, swapping them`)
      const temp = config.selectedRows
      config.selectedRows = config.selectedColumns
      config.selectedColumns = temp
    }

    // Normalize empty selections
    if (!config.selectedRows || config.selectedRows.length === 0) {
      config.selectedRows = nonBaseRows
    }
    if (!config.selectedColumns || config.selectedColumns.length === 0) {
      config.selectedColumns = nonBaseColumns
    }

    // Handle bar/column charts needing selectedColumn
    if (config.chartType === 'bar' || config.chartType === 'column') {
      if (!config.selectedColumn && config.selectedColumns && config.selectedColumns.length > 0) {
        config.selectedColumn = config.selectedColumns[0]
      }
      if (!config.selectedColumn) {
        config.selectedColumn = nonBaseColumns[0]
      }
    }

    // Validate by attempting to generate the chart
    const validationResult = generateGraphOptions(data, config)
    if (!validationResult.success) {
      console.warn(`[MultiTableGraph] Validation failed:`, validationResult.error)
      setValidationError(validationResult.error)
      setValidatedConfig(null)
    } else {
      setValidatedConfig(config)
      setValidationError(null)
    }
  }, [tableDataQuery.data, initialConfig])

  // Handle click to navigate to graph view
  const handleGraphClick = () => {
    if (!validatedConfig || !tableDataQuery.data) return

    // Close the drawer first
    onClose?.()

    // Get all rows/columns for comparison
    const allRows = extractRowLabels(tableDataQuery.data)
    const allColumns = extractColumnLabels(tableDataQuery.data)

    // Build params object
    const params: Record<string, string> = {
      view: 'graph',
      chart_type: validatedConfig.chartType,
      data_type: validatedConfig.dataType,
      switch_axis: validatedConfig.switchAxis?.toString() || 'false',
      table: tablePath.replace(/\.cbt$/, ''), // Table path without extension
    }

    // Only add rows param if not all rows are selected
    if (validatedConfig.selectedRows && validatedConfig.selectedRows.length !== allRows.length) {
      params.selected_rows = namesToIndexes(validatedConfig.selectedRows, allRows)
    }

    // Only add cols param if not all columns are selected
    if (validatedConfig.selectedColumns && validatedConfig.selectedColumns.length !== allColumns.length) {
      params.selected_columns = namesToIndexes(validatedConfig.selectedColumns, allColumns)
    }

    // Only add col param if set
    if (validatedConfig.selectedColumn) {
      params.selected_column = nameToIndex(validatedConfig.selectedColumn, allColumns)
    }

    // Clear existing graph params and merge new ones
    const currentParams = { ...router.params }
    const graphParams = ['chart_type', 'data_type', 'selected_rows', 'selected_columns', 'selected_column', 'switch_axis', 'view', 'table']
    graphParams.forEach(param => delete currentParams[param])
    const finalParams = { ...currentParams, ...params }

    // Navigate to the table with graph view
    router.navigate(router.name, finalParams)
  }

  // Loading state
  if (tableDataQuery.isLoading) {
    return (
      <Card sx={{ borderRadius: 2, border: '1px solid #00b2ff' }}>
        <CardContent sx={{ p: 0, '&:last-child': { paddingBottom: 0 } }}>
          {/* Table name header */}
          <Box sx={{
            px: 1.5,
            py: 0.75,
            borderBottom: '1px solid #00b2ff',
            backgroundColor: 'rgba(0, 178, 255, 0.05)',
          }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: '#00b2ff',
                display: 'block',
              }}
            >
              📊 {tableDisplayName}
            </Typography>
          </Box>
          <Box sx={{ p: 1.5 }}>
            {explanation && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {explanation}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', p: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading chart data...
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (tableDataQuery.isError || validationError) {
    const errorMsg = validationError || 'Failed to load table data'
    return (
      <Card sx={{ borderRadius: 2, border: '1px solid #ff0000' }}>
        <CardContent sx={{ p: 0, '&:last-child': { paddingBottom: 0 } }}>
          {/* Table name header */}
          <Box sx={{
            px: 1.5,
            py: 0.75,
            borderBottom: '1px solid #ff0000',
            backgroundColor: 'rgba(255, 0, 0, 0.05)',
          }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: '#ff0000',
                display: 'block',
              }}
            >
              📊 {tableDisplayName}
            </Typography>
          </Box>
          <Box sx={{ p: 1.5 }}>
            <Typography variant="caption" color="error">
              {errorMsg}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  // No data or config yet
  if (!tableDataQuery.data || !validatedConfig) {
    return null
  }

  const platinumData = tableDataQuery.data

  // Check for cached screenshot
  const existingScreenshots = graphScreenshots.get(messageIndex)
  const existingScreenshot = existingScreenshots?.split('|||')[graphIndex]

  if (existingScreenshot) {
    // Display cached screenshot with click handler
    return (
      <Card
        sx={{
          borderRadius: 2,
          border: '1px solid #00b2ff',
          cursor: 'pointer',
          '&:hover': { boxShadow: 3 },
        }}
        onClick={handleGraphClick}
      >
        <CardContent sx={{ p: 0, '&:last-child': { paddingBottom: 0 } }}>
          {/* Table name header */}
          <Box sx={{
            px: 1.5,
            py: 0.75,
            borderBottom: '1px solid #00b2ff',
            backgroundColor: 'rgba(0, 178, 255, 0.05)',
          }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: '#00b2ff',
                display: 'block',
              }}
            >
              📊 {tableDisplayName}
            </Typography>
          </Box>
          <Box sx={{ p: 1.5 }}>
            {explanation && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {explanation}
              </Typography>
            )}
            <PlatinumGraphScreenshot
              data={platinumData}
              config={validatedConfig}
              mode="preview"
              previewImage={existingScreenshot}
            />
          </Box>
        </CardContent>
      </Card>
    )
  }

  // Capture screenshot (also clickable once captured)
  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid #00b2ff',
        cursor: 'pointer',
        '&:hover': { boxShadow: 3 },
      }}
      onClick={handleGraphClick}
    >
      <CardContent sx={{ p: 0, '&:last-child': { paddingBottom: 0 } }}>
        {/* Table name header */}
        <Box sx={{
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid #00b2ff',
          backgroundColor: 'rgba(0, 178, 255, 0.05)',
        }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: '#00b2ff',
              display: 'block',
            }}
          >
            📊 {tableDisplayName}
          </Typography>
        </Box>
        <Box sx={{ p: 1.5 }}>
          {explanation && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {explanation}
            </Typography>
          )}
          <PlatinumGraphScreenshot
            data={platinumData}
            config={validatedConfig}
            mode="capture"
            onScreenshotReady={(base64) => {
              setGraphScreenshots(prev => {
                const next = new Map(prev)
                const existing = next.get(messageIndex)
                const screenshots = existing ? existing.split('|||') : []
                screenshots[graphIndex] = base64
                next.set(messageIndex, screenshots.join('|||'))
                return next
              })
            }}
            displayAfterCapture={true}
          />
        </Box>
      </CardContent>
    </Card>
  )
}

export default MultiTableGraph
